import { useEffect } from 'react';
import type { EmendaPublicaDTO } from '../services/transparenciaService';

// Formatação comum às páginas do Portal da Transparência. Antes cada página tinha a sua
// cópia, e só uma delas acentuava "Liquidação".

export const formatCurrency = (value?: number | null): string => value == null
  ? 'Não informado'
  : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export const formatDate = (value?: string | null): string => value
  ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
  : 'Não informado';

export const formatDateTime = (value?: string | null): string => value
  ? new Date(value).toLocaleString('pt-BR')
  : 'Não informada';

/** Percentual de `parte` sobre `todo`, sem casas quando inteiro (ex.: "50%", "6,5%"). */
export const formatPercent = (parte?: number | null, todo?: number | null): string | null => {
  if (parte == null || !todo) return null;
  const pct = (parte / todo) * 100;
  return `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

// Códigos que vêm do backend sem acento ou com sigla.
const ENUM_LABELS: Record<string, string> = {
  EMPENHO: 'Empenho',
  LIQUIDACAO: 'Liquidação',
  PAGAMENTO: 'Pagamento',
  ANULACAO: 'Anulação',
  ORIGINAL: 'Original',
  TERMO_FOMENTO: 'Termo de Fomento',
  TERMO_COLABORACAO: 'Termo de Colaboração',
  ACORDO_COOPERACAO: 'Acordo de Cooperação',
  CONVENIO: 'Convênio',
  CONTRATO: 'Contrato',
  AGUARDANDO_EMPENHO: 'Aguardando empenho',
  EMPENHADA: 'Empenhada',
  LIQUIDADA: 'Liquidada',
  PAGA_PARCIALMENTE: 'Paga parcialmente',
  PAGA: 'Paga',
  NOTA_FISCAL: 'Nota fiscal',
  MEDICAO_ATESTO: 'Medição e atesto',
  RELATORIO_EXECUCAO: 'Relatório de execução',
  FOTO: 'Foto',
  RECIBO: 'Recibo',
  COMPROVANTE: 'Comprovante',
};

export const formatEnum = (value?: string | null): string => {
  if (!value) return 'Não informado';
  const conhecido = ENUM_LABELS[value.toUpperCase()];
  if (conhecido) return conhecido;
  return value.replaceAll('_', ' ').toLocaleLowerCase('pt-BR').replace(/^./, char => char.toLocaleUpperCase('pt-BR'));
};

// Funções de governo (Portaria MOG nº 42/1999). O cadastro guarda só o código.
const FUNCOES_GOVERNO: Record<string, string> = {
  '01': 'Legislativa', '02': 'Judiciária', '03': 'Essencial à Justiça', '04': 'Administração',
  '05': 'Defesa Nacional', '06': 'Segurança Pública', '07': 'Relações Exteriores', '08': 'Assistência Social',
  '09': 'Previdência Social', '10': 'Saúde', '11': 'Trabalho', '12': 'Educação', '13': 'Cultura',
  '14': 'Direitos da Cidadania', '15': 'Urbanismo', '16': 'Habitação', '17': 'Saneamento',
  '18': 'Gestão Ambiental', '19': 'Ciência e Tecnologia', '20': 'Agricultura', '21': 'Organização Agrária',
  '22': 'Indústria', '23': 'Comércio e Serviços', '24': 'Comunicações', '25': 'Energia', '26': 'Transporte',
  '27': 'Desporto e Lazer', '28': 'Encargos Especiais',
};

export const formatFuncaoGoverno = (codigo?: string | null): string => {
  if (!codigo) return 'Não informada';
  const nome = FUNCOES_GOVERNO[codigo.trim().padStart(2, '0')];
  return nome ? `${codigo} – ${nome}` : codigo;
};

export const numeroComAno = (numero?: string | null, ano?: number | null): string => numero
  ? `${numero}${ano ? `/${ano}` : ''}`
  : 'Não informado';

/**
 * Instrumento da parceria: o número do Termo de Fomento (vindo do Coplan) tem prioridade;
 * sem ele, o número do convênio da emenda; sem nenhum, a modalidade do plano de trabalho.
 */
export const rotuloInstrumento = (emenda: EmendaPublicaDTO): string => {
  const termos = (emenda.contratos ?? []).filter(contrato => contrato.instrumento === 'TERMO_FOMENTO');
  if (termos.length > 0) return termos.map(termo => `Termo de Fomento ${numeroComAno(termo.numero, termo.ano)}`).join(', ');
  if (emenda.numeroConvenio) return numeroComAno(emenda.numeroConvenio, emenda.anoConvenio);
  return formatEnum(emenda.modalidadeInstrumento);
};

/** Processo de compra: o do Termo de Fomento tem prioridade; sem ele, o do primeiro contrato que tiver. */
export const rotuloProcesso = (emenda: EmendaPublicaDTO): string => {
  const comProcesso = (emenda.contratos ?? []).filter(contrato => contrato.processoCompraNumero);
  const escolhido = comProcesso.find(contrato => contrato.instrumento === 'TERMO_FOMENTO') ?? comProcesso[0];
  return escolhido ? numeroComAno(escolhido.processoCompraNumero, escolhido.processoCompraAno) : 'Não informado';
};

/** Título da aba: cada página pública tem o seu, para favoritos, compartilhamento e leitor de tela. */
export const usePortalTitle = (titulo?: string | null) => {
  useEffect(() => {
    const anterior = document.title;
    document.title = titulo ? `${titulo} · Portal da Transparência` : 'Portal da Transparência';
    return () => { document.title = anterior; };
  }, [titulo]);
};
